<template>
  <vx-card class="bar-chart-card" style="overflow-x: auto; max-width: 100%">
    <vs-row>
      <vs-col style="position: relative">
        <!-- header tools -->
        <div class="bar-chart-card-header">
          <span class="font-bold" style="font-size: 1em; align-self: center"> <i class="dot"></i>{{ title }} </span>
          <div style="justify-content: end">
            <vs-select v-model="selectedYear" class="w-24" @change="loadData">
              <vs-select-item v-for="option of yearOptions" :key="option.value" :value="option.value" :text="option.label" />
            </vs-select>
          </div>
        </div>

        <!-- chart container with proper height -->
        <div class="chart-wrapper">
          <div ref="chartContainer" class="chart-container">
            <apexchart type="bar" :width="containerWidth" height="380" :options="barOptions.chartOptions" :series="barOptions.series"></apexchart>
          </div>
        </div>
      </vs-col>
    </vs-row>
  </vx-card>
</template>

<script>
export default {
  name: "BarChart310",
  props: {
    title: {
      type: String,
      default: "사업장별 재해현황",
    },
    unit: {
      type: String,
      default: "건",
    },
    value: {
      type: Array,
      default: () => [{ label: "데이터 없음", value: 0 }],
    },
    isMoveDetail: {
      type: Boolean,
      default: true,
    },
    isMoveWrite: Boolean,
  },
  data() {
    return {
      selectedYear: new Date().getFullYear(),
      chartData: [],
      sumCount: 0,
      maxCount: 0,
      // 공장별 데이터
      factoryData: {},
      // 공장별 소계 데이터
      containerWidth: "100%",
    };
  },
  computed: {
    projectId() {
      return this.$route.params.proj_id;
    },
    myLv() {
      return this.$store.state.auth.userInfo.position;
    },

    /**
     * 최근 5년부터 내년까지 옵션
     */
    yearOptions() {
      const currentYear = new Date().getFullYear();
      const startYear = currentYear - 5;
      const options = [];
      for (let i = 0; i < 7; i++) {
        options.push({ label: `${startYear + i}년`, value: startYear + i });
      }

      return options.reverse();
    },
    /**
     * 최대 값보다 10% 더 큰 값으로 y축 최대값 설정
     */
    maxY() {
      return Math.ceil(this.maxCount * 1.1);
    },

    barOptions() {
      const series = [];
      const defaultCategories = ["인천공장", "포항공장", "당진공장", "중앙기술연구소", "본사"];

      series.push({
        name: "직영",
        data: defaultCategories.map((factory) => (this.factoryData[factory] && this.factoryData[factory]["직영"]) || 0),
      });
      series.push({
        name: "사내수급",
        data: defaultCategories.map((factory) => (this.factoryData[factory] && this.factoryData[factory]["사내수급"]) || 0),
      });
      series.push({
        name: "사외수급",
        data: defaultCategories.map((factory) => (this.factoryData[factory] && this.factoryData[factory]["사외수급"]) || 0),
      });

      return {
        series,
        chartOptions: {
          chart: {
            type: "bar",
            stacked: true,
            toolbar: { show: false },
            parentHeightOffset: 0,
          },
          plotOptions: {
            bar: {
              horizontal: false,
              columnWidth: "55%",
              dataLabels: {
                position: "center",
                total: {
                  enabled: true,   // ✅ 합계 표시
                  offsetY: -10,
                  style: { fontSize: "13px", fontWeight: "bold", color: "#000" },
                },
              },
            },
          },
          dataLabels: {
            enabled: true,
            style: { fontSize: "12px", colors: ["#fff"] },
          },
          stroke: { show: true, width: 2, colors: ["transparent"] },
          colors: ["#0058FF", "#21D59B", "#FFC700"],
          xaxis: {
            categories: defaultCategories,
            labels: { rotate: 0, trim: true, style: { fontSize: "11px" } },
          },
          yaxis: {
            stepSize: 1,
            max: this.maxY,
            show: false,
          },
          fill: { opacity: 1 },
          tooltip: {
            y: {
              formatter: function (val) {
                return `${val} ${this.unit}`;
              }.bind(this),
            },
          },
          legend: { position: "bottom" },
        },
      };
    },
  },

  mounted() {
    this.loadData();

    this.updateContainerWidth();
    window.addEventListener("resize", this.updateContainerWidth);
  },
  beforeDestroy() {
    // 컴포넌트가 제거될 때 이벤트 리스너 제거
    window.removeEventListener("resize", this.updateContainerWidth);
  },

  methods: {
    onClickDetail() {
      this.$emit("detail");
    },
    onClickWrite() {
      this.$emit("write");
    },
    onClickItem(data) {
      this.$emit("item", data);
    },
    updateContainerWidth() {
      // nextTick을 사용하여 DOM 업데이트 이후에 너비를 측정
      this.$nextTick(() => {
        if (this.$refs.chartContainer) {
          // 정수 값으로 설정(px 없이)
          this.containerWidth = this.$refs.chartContainer.offsetWidth;
        }
      });
    },
    async loadData() {
      const projectId = this.projectId;
      const boardId = 823;
      const page = 1;
      const perPage = 1000;

      const conditions = [];
      conditions.push({ key: "body.subInfo.재해일시", operator: ">=", value: `${this.selectedYear}-01-01` });
      conditions.push({ key: "body.subInfo.재해일시", operator: "<=", value: `${this.selectedYear}-12-31` });
      conditions.push({ key: "body.subInfo.산재승인여부", operator: "=", value: "승인" });

      const response = await this.$store.dispatch("board/LOAD_POST_LIST", {
        projectId,
        boardId,
        page,
        perPage,
        conditions,
        withMeta: true,
        metaKeys: ["body.subInfo.구분"],
        onlyMeta: true,
      });

      const allData = response.data || [];

      // 공장별, 구분별 데이터 집계
      const factoryData = {};
      this.sumCount = 0;

      for (const data of allData) {
        const content = data.postContents || {};
        if (content.body && content.body.subInfo.구분) {
          const type = content.body.subInfo.구분; // 직영, 사내수급, 사외수급
          const fieldName = data.field ? data.field.field_name : "기타"; // 공장명 (인천공장, 포항공장 등)

          // 공장별 데이터 초기화
          if (!factoryData[fieldName]) {
            factoryData[fieldName] = {
              직영: 0,
              사내수급: 0,
              사외수급: 0,
            };
          }

          if (factoryData[fieldName][type] !== undefined) {
            factoryData[fieldName][type] += 1;
          }

          this.sumCount += 1;
        }
      }
      const defaultCategories = ["인천공장", "포항공장", "당진공장", "중앙기술연구소", "본사"];
      const maxTotal = defaultCategories.reduce((acc, factory) => {
        const f = factoryData[factory] || { 직영: 0, 사내수급: 0, 사외수급: 0 };
        const sum = (f.직영 || 0) + (f.사내수급 || 0) + (f.사외수급 || 0);
        return Math.max(acc, sum);
      }, 0);

      this.maxCount = maxTotal;
      this.factoryData = factoryData;
    },
  },
};
</script>

<style lang="scss" scoped>
.bar-chart-card {
  position: relative;
}

.bar-chart-card-header {
  display: flex;
  justify-content: space-between;
  align-items: start;
  position: relative;
  top: 0;
  left: 0;
  right: 0;
  margin-bottom: 20px;
  z-index: 10;
}

.chart-wrapper {
  width: 100%;
  min-height: 400px;
  display: flex;
  justify-content: center;
  align-items: center;
}

.chart-container {
  width: 100%;
  max-width: 100%;
  position: relative;
}

.dot {
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: #7367f0;
  margin-right: 5px;
}

.factory-totals {
  display: flex;
  justify-content: space-between;
  margin-top: 15px;
}

.factory-total-item {
  text-align: center;
  flex: 1;
}

.factory-name {
  font-size: 0.8rem;
  color: #666;
}

.factory-total {
  font-weight: bold;
  font-size: 0.9rem;
}

@media (max-width: 768px) {
  .bar-chart-card {
    margin: 0 6px !important;
    width: calc(100% - 12px);
  }

  .bar-chart-card-header {
    flex-direction: row;
    gap: 10px;
    margin-bottom: 15px;
    justify-content: space-between;
  }

  .chart-wrapper {
    min-height: 350px;
    margin-top: 10px;
  }

  .chart-container {
    overflow-x: auto;
    overflow-y: hidden;
  }
}

@media (max-width: 480px) {
  .chart-wrapper {
    min-height: 320px;
  }

  .bar-chart-card-header {
    margin-bottom: 10px;
  }
}
</style>
